import Link from 'next/link';
import { Facebook, Instagram, Linkedin, Twitter } from 'lucide-react';

import {
  EXTERNAL_URLS,
  SOCIAL_PLATFORMS,
  SUPPORT_CONTACTS,
} from '@/shared/constants/externalLinks';

const socialLinks = [
  {
    label: 'Motito on Instagram',
    href: 'https://instagram.com/motitohq',
    Icon: Instagram,
  },
  {
    label: 'Motito on Twitter',
    href: 'https://twitter.com/motitohq',
    Icon: Twitter,
  },
  {
    label: 'Motito on Facebook',
    href: 'https://facebook.com/motitohq',
    Icon: Facebook,
  },
  {
    label: 'Motito on LinkedIn',
    href: 'https://www.linkedin.com/company/motito/',
    Icon: Linkedin,
  },
];

export default function Footer() {
  const currentYear = new Date().getUTCFullYear();
  const whatsappUrl = SOCIAL_PLATFORMS.WHATSAPP.chatUrl(
    SUPPORT_CONTACTS.BETTY_WHATSAPP
  );

  return (
    <section className="hidden bg-motitoBlack text-white md:block">
      <div className="container mt-20 py-16 lg:py-20">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1.2fr)_minmax(360px,0.8fr)] lg:items-start">
          <div>
            <h2 className="max-w-[8.6ch] font-garnettSemiBold text-5xl leading-[0.94] md:text-6xl lg:text-7xl">
              Start shopping today.
            </h2>
            <p className="mt-4 max-w-[30rem] text-base leading-7 text-white/70">
              Pay small small for phones, laptops, and more. No interest. No
              wahala.
            </p>
            <div className="mt-7">
              <Link
                href="/"
                className="inline-flex h-12 items-center justify-center rounded-full bg-white px-6 text-sm font-semibold text-motitoBlack transition hover:bg-white/90"
              >
                Start shopping
              </Link>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              {socialLinks.map(({ label, href, Icon }) => (
                <Link
                  key={href}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="inline-flex size-11 items-center justify-center rounded-full border border-white/15 text-white/80 transition hover:border-white/40 hover:text-white"
                >
                  <Icon aria-hidden="true" size={20} strokeWidth={2} />
                </Link>
              ))}
            </div>
          </div>

          <nav
            aria-label="Footer navigation"
            className="grid grid-cols-3 gap-x-8 gap-y-7"
          >
            <div className="grid content-start gap-3">
              <h3 className="text-xs font-bold uppercase tracking-[0.12em] text-white/50">
                Company
              </h3>
              <Link className="text-white/80 hover:text-white" href="/about">
                Why Motito
              </Link>
              <Link
                className="text-white/80 hover:text-white"
                href={EXTERNAL_URLS.MARKETING.BLOG}
                target="_blank"
                rel="noopener noreferrer"
              >
                Blog
              </Link>
            </div>

            <div className="grid content-start gap-3">
              <h3 className="text-xs font-bold uppercase tracking-[0.12em] text-white/50">
                Resources
              </h3>
              <Link className="text-white/80 hover:text-white" href="/">
                Shop
              </Link>
              <Link className="text-white/80 hover:text-white" href="/faqs">
                FAQ
              </Link>
              <Link
                className="text-white/80 hover:text-white"
                href={EXTERNAL_URLS.HELP_CENTER}
                target="_blank"
                rel="noopener noreferrer"
              >
                Help Centre
              </Link>
              <Link
                className="text-white/80 hover:text-white"
                href={`mailto:${SUPPORT_CONTACTS.SUPPORT_EMAIL}`}
              >
                Partner with us
              </Link>
            </div>

            <div className="grid content-start gap-3">
              <h3 className="text-xs font-bold uppercase tracking-[0.12em] text-white/50">
                Contact
              </h3>
              <Link
                className="text-white/80 hover:text-white"
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                WhatsApp
              </Link>
              <Link
                className="text-white/80 hover:text-white"
                href={`mailto:${SUPPORT_CONTACTS.SUPPORT_EMAIL}`}
              >
                {SUPPORT_CONTACTS.SUPPORT_EMAIL}
              </Link>
            </div>
          </nav>
        </div>

        <div className="mt-10 border-t border-white/10 pt-6">
          <div className="flex flex-wrap items-center justify-between gap-4 text-sm text-white/55">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <Link
                className="hover:text-white"
                href={EXTERNAL_URLS.LEGAL.TERMS}
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms &amp; Conditions
              </Link>
              <span aria-hidden="true">•</span>
              <Link
                className="hover:text-white"
                href={EXTERNAL_URLS.LEGAL.PRIVACY}
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </Link>
              <span aria-hidden="true">•</span>
              <span>BoG Recognised</span>
            </div>
            <p suppressHydrationWarning>
              © {currentYear} Motito Ltd. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
